import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'
import { apiSlice } from '@/app/api/apiSlice'


// 🔹 Canonical Conversation Master Adapter (16 Jan 2026)
const userConversationMastersAdapter = createEntityAdapter({})

const initialConversationMasterState =
    userConversationMastersAdapter.getInitialState()

const userConversationsAdapter = createEntityAdapter({})
const initialState = userConversationsAdapter.getInitialState()

export const userConversationsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getUserConversations: builder.query({
            query: () => ({
                url: '/userConversations/getUserConversations',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                }
            }),
            transformResponse: responseData => {
                const loadedUserConversations = responseData.map(userConversation => {
                    userConversation.id = userConversation._id
                    return userConversation
                })
                return userConversationsAdapter.setAll(initialState, loadedUserConversations)
            },
            providesTags: (result, error, arg) => {
                if(result?.ids) {
                    return [
                        {type: 'UserConversation', id: 'LIST'},
                        ...result.ids.map(id => ({ type: 'UserConversation', id}))
                    ]
                } else {
                    return [ { type: 'UserConversation', id: 'LIST'}]
                }
            },
        }),
        createNewUserConversation: builder.mutation({
            query: initialUserConversation => ({
                url: '/userConversations/createNewUserConversation',
                method: 'POST',
                body: {...initialUserConversation,}
            }),
            invalidatesTags: [
                { type: 'UserConversation', id: "LIST" }
            ] 
        }),
        updateUserConversation: builder.mutation({
            query: initialUserConversation => ({
                url: '/userConversations/updateUserConversation',
                method: 'PATCH',
                body: {...initialUserConversation,}
            }),
            invalidatesTags: (result, error, arg) => [
                {type: 'UserConversation', id: arg.id },
                { type: 'UserConversation', id: 'LIST' }
            ],
        }),
        deleteUserConversation: builder.mutation({
            query: ({id}) => ({
                url: '/userConversations/deleteUserConversation',
                method: 'DELETE',
                body: {id}
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'UserConversation', id: arg.id}
            ]
        }),
        // 06 Jun 2025
        saveInsightChatForContactNote: builder.mutation({
            query: ({ userId, userConversationId, contactNoteId, chatHistory, generatedInsightHtml }) => ({
                url: '/userConversations/saveInsightChat',
                method: 'POST',
                body: {
                    userId,
                    userConversationId,
                    contactNoteId,
                    chatHistory,
                    generatedInsightHtml
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'UserConversation', id: arg.userConversationId }
            ],
        }),
        // 09 Jun 2025
        generateTrinityActionsForNote: builder.mutation({
            query: ({ conversationId, noteId, userId, userOrgId }) => ({
                url: '/userConversations/generateTrinityActionsForNote',
                method: 'POST',
                body: { conversationId, noteId, userId, userOrgId },
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'UserConversation', id: arg.conversationId },
            ],
        }),
        // --------------------------------------------------
        // 18 Jan 2026 — Canonical Conversation Master (All with scope)
        // --------------------------------------------------
        // getUserConversationMasterAlls: builder.query({
        //     query: ({ scope = 'self' } = {}) => ({
        //         url: '/userConversations/getUserConversationMasterAlls',
        //         params: { scope }, // 🔑 NEW
        //         validateStatus: (response, result) =>
        //             response.status === 200 && !result?.isError,
        //     }),
        //     transformResponse: responseData => {
        //         const loadedMasters = responseData.map(record => {
        //             record.id = record._id
        //             return record
        //         })
        //         return userConversationMastersAdapter.setAll(
        //             initialConversationMasterState,
        //             loadedMasters
        //         )
        //     },
        //     providesTags: (result, error, arg) => {
        //         const scope = arg?.scope || 'self'
        //         if (result?.ids) {
        //             return [
        //                 { type: 'UserConversationMaster', id: `LIST-${scope}` },
        //                 ...result.ids.map(id => ({
        //                     type: 'UserConversationMaster',
        //                     id,
        //                 })),
        //             ]
        //         }
        //         return [{ type: 'UserConversationMaster', id: `LIST-${scope}` }]
        //     },
        // }),

        // --------------------------------------------------
        // 16 Jan 2026 — Canonical Conversation Master (All)
        // --------------------------------------------------
        getUserConversationMasterAlls: builder.query({
            query: () => ({
                url: '/userConversations/getUserConversationMasterAlls',
                validateStatus: (response, result) =>
                    response.status === 200 && !result?.isError,
            }),
            // 26 Jan 2026 - To allow new backend data for Universe calc
            // 🟢 Handle sponsor + non-sponsor payloads
            transformResponse: (responseData) => {

                // 🟢 Sponsor path → backend sends object
                if (responseData?.conversations) {
                return {
                    conversations: responseData.conversations.map((c) => ({
                    ...c,
                    id: c._id,
                    })),
                    orgDailyTotals: responseData.orgDailyTotals || [],
                    orgUserDailyTotals: responseData.orgUserDailyTotals || [], // 31 Jan 2026
                    orgAccounts: responseData.orgAccounts || [], // 29 Jun 2026
                };
                }

                // 🟢 Non-sponsor / legacy path → backend sends array
                if (Array.isArray(responseData)) {
                return responseData.map((c) => ({
                    ...c,
                    id: c._id,
                }));
                }

                // 🟢 Fallback safety
                return [];
            },

            // 🟢 Provider tags (aligned with Reflections)
            providesTags: (result) => {
                const conversations = Array.isArray(result)
                ? result
                : result?.conversations || [];

                return [
                { type: 'UserConversationMaster', id: 'LIST' },
                ...conversations.map((c) => ({
                    type: 'UserConversationMaster',
                    id: c._id,
                })),
                ];
            },

            // 26 Jan 2026 - Prior Code - Commented out - DO NOT DELETE - KEPT FOR REFERENCE
            // transformResponse: responseData => {
            //     const loadedMasters = responseData.map(record => {
            //         record.id = record._id
            //         return record
            //     })
            //     return userConversationMastersAdapter.setAll(
            //         initialConversationMasterState,
            //         loadedMasters
            //     )
            // },
            // providesTags: (result) => {
            //     if (result?.ids) {
            //         return [
            //             { type: 'UserConversationMaster', id: 'LIST' },
            //             ...result.ids.map(id => ({
            //                 type: 'UserConversationMaster',
            //                 id,
            //             })),
            //         ]
            //     }
            //     return [{ type: 'UserConversationMaster', id: 'LIST' }]
            // },
        }),

    }),
})

export const {
    useGetUserConversationsQuery,
    useCreateNewUserConversationMutation,
    useUpdateUserConversationMutation,
    useDeleteUserConversationMutation,
    useSaveInsightChatForContactNoteMutation,  // ✅ new - 06 Jun 2025
    useGenerateTrinityActionsForNoteMutation,  // ✅ new - 09 Jun 2025

    // ✅ 16 Jan 2026 — Conversation Master
    useGetUserConversationMasterAllsQuery,
} = userConversationsApiSlice

// returns the query result object
export const selectUserConversationResult = userConversationsApiSlice.endpoints.getUserConversations.select()

// create memoized selector
const selectUserConversationsData = createSelector(
    selectUserConversationResult,
    userConversationResult => userConversationResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllUserConversations,
    selectById: selectUserConversationById,
    selectIds: selectUserConversationIds,
} = userConversationsAdapter.getSelectors(state => selectUserConversationsData(state) ?? initialState)

// --------------------------------------------------
// Conversation Master Selectors (16 Jan 2026)
// --------------------------------------------------
export const selectUserConversationMasterResult =
    userConversationsApiSlice.endpoints.getUserConversationMasterAlls.select()

const selectUserConversationMasterData = createSelector(
    selectUserConversationMasterResult,
    result => result?.data
)

export const {
    selectAll: selectAllUserConversationMasters,
    selectById: selectUserConversationMasterById,
    selectIds: selectUserConversationMasterIds,
} = userConversationMastersAdapter.getSelectors(
    state => selectUserConversationMasterData(state) ?? initialConversationMasterState
)
